import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  Phone,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  PartyPopper,
  Mic,
  MicOff,
  Minus,
  User,
  ScrollText,
  Check,
  X,
} from 'lucide-react';

import {
  sourceIdentity,
  scriptForStep,
  renderScript,
  resolveScriptVariant,
} from '../boardUtils';
import { buildCalendlyUrl } from '../../../../lib/calendlyLink';
import { useFetchLeadSignup } from '../../../../services/onboardingBoard';
import { deriveProgress } from '../signup/SignupFlyover';
import CallWorkspace from './CallWorkspace';
import { vaGovReadyAtSigning } from './HotSeatTransferCard';

// The Call Command Center v3 (Nic 2026-08-12): ONE solid surface, anchored
// dead center, with a HARD rule — the card never changes size, vertically or
// horizontally, in any call scenario. Every view lives inside the same fixed
// footprint; actions are icon discs instead of stacked rows; anything that
// needs extra space opens the SOUTH pop-out (same animation language as the
// side wings) instead of growing the card. All components are TOP-LEVEL:
// inline component definitions remount on every timer tick and replay their
// animations (the "blinking" bug).

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return p || '';
};

const formatDuration = (ms) => {
  const s = Math.floor(ms / 1000);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
};

const initialsOf = (first, last) =>
  `${(first || '?')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();

const HeaderToggle = ({ icon: Icon, open, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`rounded-lg p-1.5 transition-colors ${
      open
        ? 'bg-slate-900 text-white dark:bg-slate-200 dark:!text-slate-900'
        : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10'
    }`}
  >
    <Icon size={15} />
  </button>
);

// ---- Wings ------------------------------------------------------------------

const PersonWing = ({ lead }) => {
  const src = sourceIdentity(lead);
  const snap = lead?.leadMeta?.veteranSnapshot || null;
  const Row = ({ label, value }) =>
    value ? (
      <div className="flex justify-between gap-2 text-xs">
        <span className="text-slate-400">{label}</span>
        <span className="text-right font-medium text-slate-600 dark:text-slate-300">
          {value}
        </span>
      </div>
    ) : null;
  return (
    <div className="w-64 space-y-3 p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        The person
      </div>
      <div>
        <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
          {lead.leadFirstName} {lead.leadLastName}
        </div>
        <div className="text-xs text-slate-400">
          {formatPhone(lead.leadPhone)}
        </div>
        {lead.leadEmail ? (
          <div className="truncate text-xs text-slate-400">
            {lead.leadEmail}
          </div>
        ) : (
          <div className="text-xs italic text-slate-400">
            No email on record
          </div>
        )}
      </div>
      {src?.label ? (
        <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs dark:bg-white/5">
          <span className="font-semibold text-slate-600 dark:text-slate-300">
            {src.label}
          </span>
          {src.detail ? (
            <span className="text-slate-400"> · {src.detail}</span>
          ) : null}
        </div>
      ) : null}
      {snap ? (
        <div className="space-y-1.5 rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-white/5">
          <Row
            label="Rating"
            value={
              snap.currentVaPercentage != null
                ? `${snap.currentVaPercentage}%`
                : null
            }
          />
          <Row label="Branch" value={snap.branch} />
          <Row label="Rank" value={snap.rankPaygrade} />
          <Row
            label="Location"
            value={
              snap.location?.city
                ? `${snap.location.city}, ${snap.location.state || ''}`
                : null
            }
          />
          <Row label="Open claim" value={snap.openClaim} />
        </div>
      ) : null}
      {lead.leadWorkNotes ? (
        <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          {lead.leadWorkNotes}
        </div>
      ) : null}
    </div>
  );
};

const ScriptWing = ({ call, user }) => {
  const { step, run, boardContext } = call;
  const script = scriptForStep(step, boardContext?.scripts || {});
  // The lead's partner may have its own wording — read it aloud, not the
  // default (resolveScriptVariant falls back when no version matches).
  const content = resolveScriptVariant(script, run.lead);
  const rendered = script
    ? renderScript(content.body, {
        lead: run.lead,
        user,
        assigneeNumber: run.ownerPhone,
      })
    : null;
  return (
    <div className="w-72 p-4">
      {/* Always "VM script" (Nic 2026-08-12): on a call, the script IS the
          voicemail script — "Call script" misread as talking points. */}
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        VM script · {step.label || step.stepKey}
      </div>
      {rendered ? (
        <div className="whitespace-pre-wrap rounded-xl bg-slate-50 px-3.5 py-3 text-xs leading-relaxed text-slate-600 dark:bg-white/5 dark:text-slate-300">
          {rendered}
        </div>
      ) : call.followUp ? (
        <div className="rounded-xl bg-slate-50 px-3.5 py-3 text-xs leading-relaxed text-slate-500 dark:bg-white/5 dark:text-slate-400">
          According to the records, this is a follow-up call. You can leave a
          voicemail if you&apos;d like. There&apos;s no set script.
        </div>
      ) : call.outsideCadence ? (
        <div className="rounded-xl bg-slate-50 px-3.5 py-3 text-xs leading-relaxed text-slate-500 dark:bg-white/5 dark:text-slate-400">
          This call is outside the cadence. Nothing here counts toward the
          checklist. There&apos;s no set script.
        </div>
      ) : (
        <div className="rounded-xl bg-amber-50 px-3.5 py-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          Do not leave a voicemail on this call. If they don&apos;t answer, hang
          up and log the outcome.
        </div>
      )}
    </div>
  );
};

// ---- The in-card outcome question (same rules as ever: not skippable until
// answered, "Didn't go through" leaves the step actionable, a 409 means the
// question is moot and the center closes itself). The header carries the
// question; the body is a compact 2-column grid — fixed footprint. ----------

const OutcomeChoice = ({
  icon: Icon,
  label,
  sub,
  onClick,
  accent,
  busy,
  className = '',
}) => (
  <button
    type="button"
    disabled={busy}
    onClick={onClick}
    className={`flex items-center gap-2.5 rounded-xl bg-slate-50 px-3 py-2.5 text-left transition-colors hover:bg-slate-100 disabled:opacity-50 dark:bg-white/5 dark:hover:bg-white/10 ${className}`}
  >
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${accent}`}
    >
      <Icon size={14} className="text-white" />
    </span>
    <span className="min-w-0">
      <span className="block text-[13px] font-semibold leading-snug text-slate-800 dark:text-slate-100">
        {label}
      </span>
      {sub ? (
        <span className="block text-[11px] leading-snug text-slate-400">
          {sub}
        </span>
      ) : null}
    </span>
  </button>
);

// The mid-call "Made contact" button gets the same two-tap arm as the
// wrap-up's "Reached them" (Nic 2026-08-17): first tap asks, second within
// 5s commits, ignoring it disarms.
const MadeContactButton = ({ busy, dialing, onMadeContact }) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      disabled={busy || dialing}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        onMadeContact();
      }}
      className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white transition-colors disabled:opacity-50 ${
        armed
          ? 'bg-amber-500 hover:bg-amber-400'
          : 'bg-emerald-600 hover:bg-emerald-500'
      }`}
    >
      <PhoneCall size={16} />{' '}
      {armed ? 'You sure? Tap again to confirm' : 'Made contact'}
    </button>
  );
};

const OutcomeInline = ({ busy, actions, stage, setStage }) => {
  const [outreachId, setOutreachId] = useState(null);
  // "Reached them" got mis-clicked twice on day one (Nic 2026-08-17), and
  // picking it observes the contact server-side BEFORE the follow-up
  // question — so the guard belongs here: first tap arms, second tap within
  // 5s commits, ignoring it disarms. The harmless outcomes stay one-tap.
  const [armReached, setArmReached] = useState(false);
  useEffect(() => {
    if (!armReached) return undefined;
    const t = setTimeout(() => setArmReached(false), 5000);
    return () => clearTimeout(t);
  }, [armReached]);

  const pick = async (outcome) => {
    let result;
    try {
      result = await actions.recordCallOutcome(outcome);
    } catch (err) {
      if (err?.response?.status === 409) actions.closeCenter();
      return;
    }
    if (outcome === 'reached') {
      // `result` is null for an outside-cadence call: there is no step to
      // grade (the call itself was already logged at placement), but
      // reaching them still deserves the contact pivot — this used to close
      // silently and skip the "mark contact made?" question entirely.
      setOutreachId(result?.leadOutreachId || null);
      setStage('contact');
    } else {
      // Nothing left to record: with a step, the outcome was just stamped;
      // without one, the call was logged at placement and an unanswered
      // outside call carries no further state.
      actions.closeCenter();
    }
  };

  const discard = async () => {
    try {
      await actions.recordDiscard();
    } catch (err) {
      if (err?.response?.status === 409) actions.closeCenter();
      return;
    }
    actions.closeCenter();
  };

  const notYet = () => {
    actions.contactObserved(outreachId);
    actions.closeCenter();
  };

  if (stage === 'contact') {
    return (
      <div className="cc-fade-in my-auto space-y-3">
        <p className="text-center text-xs text-slate-400">
          Any real conversation stops the cadence and pivots to booking the
          call.
        </p>
        <div className="flex justify-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => actions.contactMadeFromSheet(outreachId)}
            className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            Yes, mark contact made
          </button>
          <button
            type="button"
            onClick={notYet}
            className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-300 dark:hover:!bg-white/15"
          >
            Not yet
          </button>
        </div>
      </div>
    );
  }

  // Three answers (Nic 2026-08-12): voicemail-or-not doesn't need its own
  // option — the checklist marks the step and the VM script sat right there.
  // "No answer" covers both.
  return (
    <div className="my-auto grid grid-cols-2 gap-2">
      <OutcomeChoice
        icon={PhoneCall}
        label={armReached ? 'You sure?' : 'Reached them'}
        sub={
          armReached ? 'We actually spoke, tap again to confirm' : 'We spoke'
        }
        accent={armReached ? 'bg-amber-500' : 'bg-emerald-500'}
        onClick={() => {
          if (!armReached) {
            setArmReached(true);
            return;
          }
          setArmReached(false);
          pick('reached');
        }}
        busy={busy}
      />
      <OutcomeChoice
        icon={PhoneMissed}
        label="No answer"
        sub="They didn't pick up"
        accent="bg-slate-400"
        onClick={() => pick('no_answer')}
        busy={busy}
      />
      <OutcomeChoice
        icon={PhoneOff}
        label="Didn't go through"
        sub="Didn't ring / Disconnected"
        accent="bg-amber-500"
        onClick={discard}
        busy={busy}
        className="col-span-2"
      />
    </div>
  );
};

// ---- The center ------------------------------------------------------------

const CallCommandCenter = ({ call, user, muted, elapsedMs, busy, actions }) => {
  const [leftOpen, setLeftOpen] = useState(false);
  // Closed by default (Nic 2026-08-12) — the script is one click away.
  const [rightOpen, setRightOpen] = useState(false);
  const [outcomeStage, setOutcomeStage] = useState('outcome'); // outcome | contact

  const { lead, run, step, boardContext } = call;
  // Signup progress drives the chooser and the wrap-up (Nic 2026-09-23: once
  // they sign, the book / send / not-interested choices go). Shares the
  // flyover's query key, so an open flyover and this stay in lockstep.
  const signupQuery = useFetchLeadSignup(lead?.leadId, {
    enabled: !!lead?.leadId && !!call.contactMade,
  });
  const signup = deriveProgress(signupQuery.data);
  const targets = (boardContext?.transferTargets || []).filter(
    (t) => t.userId !== boardContext?.viewerUserId,
  );
  // Live poll first (a booking made DURING this call), run snapshot second.
  // Only a meeting still AHEAD counts as booked (Nic 2026-09-30, same rule
  // as the side panel). Once it has started (QA 2026-10-26, was: once it
  // ended) the rep can book the next one on this very call, as the server
  // allows.
  const stillAhead = (b) =>
    !!b?.startTime && new Date(b.startTime).getTime() > Date.now();
  const snapshotBooking =
    run?.meetingTrail?.next || run?.booking || null;
  // Their meeting as it stands now (Nic 2026-10-26): a Cancel or Reschedule
  // from the Workspace refreshes it at once (every meeting change refreshes
  // the ['onboarding-board'] queries), where the open call's snapshot kept
  // the old meeting until the Workspace was reopened. Until it loads, the
  // live poll and the snapshot answer as before.
  const currentQ = useQuery(
    ['onboarding-board', 'lead-booking', lead?.leadId],
    async () => (await axios.get(`/onboarding-board/leads/${lead.leadId}/live-booking`)).data,
    { enabled: !!lead?.leadId && !call.client, staleTime: 5000 },
  );
  const current = currentQ.data ? currentQ.data.booking : undefined;
  const detectedBooking =
    current !== undefined
      ? current && current.status === 'active' && stillAhead(current)
        ? current
        : null
      : call.liveBooking ||
        (snapshotBooking &&
        snapshotBooking.status === 'active' &&
        stillAhead(snapshotBooking)
          ? snapshotBooking
          : null);
  // Meetings book on the LEAD OWNER's own Calendly (2026-09-23), even
  // when someone else (a manager) is on the call.
  const ownerCalendlyUrl = run?.ownerCalendlyUrl || '';
  const calendlyUrl = ownerCalendlyUrl
    ? buildCalendlyUrl(ownerCalendlyUrl, lead, {
        source: 'board',
        senderUserId: boardContext?.viewerUserId || null,
      })
    : null;

  const softWrapup =
    call.placed &&
    !call.callLive &&
    call.contactMade &&
    !call.declaredOutcome &&
    !call.transferStaged;

  const statusLabel = call.failed
    ? 'Call failed'
    : !call.placed
      ? 'Ready to call'
      : call.dialing
        ? 'Calling...'
        : call.callLive
          ? call.contactMade
            ? 'Contact made'
            : 'On the line'
          : 'Call ended';

  const dotClass = call.failed
    ? 'bg-red-500'
    : !call.placed
      ? 'bg-slate-300'
      : call.callLive
        ? call.contactMade
          ? 'bg-emerald-500'
          : 'animate-pulse bg-emerald-500'
        : 'bg-slate-300';

  // A distinct key per view drives the crossfade (real transitions only).
  const viewKey = call.failed
    ? 'failed'
    : !call.placed
      ? 'precall'
      : call.callLive && call.consult
        ? 'consult'
        : call.callLive && !call.contactMade
          ? 'talking'
          : call.callLive
            ? 'actions'
            : call.transferStaged
              ? 'transfer'
              : softWrapup
                ? 'wrapup'
                : 'outcome';

  const wingShell =
    'absolute top-0 bottom-0 overflow-hidden bg-white shadow-xl transition-all duration-200 ease-out dark:!bg-surface-elevated';

  // After contact is made, the small card opens into the full-screen call
  // workspace (Nic 2026-09-23): the talk track, the person, notes, signup
  // inline, and a next-step bar. The card keeps only the pre-contact job
  // (dial, talk, Made contact) and the landed-transfer confirmation.
  // A follow-up call (contact already made, e.g. Call from Potential
  // Clients) skips the small card entirely (Nic 2026-09-24): the workspace
  // opens at once and Place call sits in its top bar.
  if (
    call.contactMade &&
    (viewKey === 'actions' ||
      viewKey === 'consult' ||
      viewKey === 'wrapup' ||
      (call.followUp && (viewKey === 'precall' || viewKey === 'failed')))
  ) {
    return (
      <CallWorkspace
        call={call}
        muted={muted}
        elapsedMs={elapsedMs}
        busy={busy}
        actions={actions}
        signup={signup}
        detectedBooking={detectedBooking}
        calendlyUrl={calendlyUrl}
        targets={targets}
        specialist={signupQuery.data?.specialist || null}
        vaGovReady={
          signupQuery.data?.vaGovHotSeat
            ? signupQuery.data.vaGovHotSeat.ready
            : vaGovReadyAtSigning(signupQuery.data)
        }
        vaGovJustDone={!!signupQuery.data?.vaGovHotSeat?.clientSaidDone}
        liveCall={signupQuery.data?.liveCall || null}
      />
    );
  }

  return createPortal(
    // z-1078: above the board slide-over (1060), below the dock/orphan layer.
    <div className="cc-root fixed inset-0 z-[1078]">
      <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60" />

      {/* THE ANCHOR: the center is pinned here and never moves. Wings hang
          off its edges, the south dock off its bottom — all one surface. */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        {/* Left wing */}
        <div
          className={`${wingShell} right-full rounded-l-2xl border-r border-slate-100 dark:border-white/10 ${
            leftOpen ? 'w-64 opacity-100' : 'w-0 opacity-0'
          }`}
        >
          <div className="os-scroll h-full w-64 overflow-y-auto overflow-x-hidden">
            <PersonWing lead={lead} />
          </div>
        </div>

        {/* Right wing */}
        <div
          className={`${wingShell} left-full rounded-r-2xl border-l border-slate-100 dark:border-white/10 ${
            rightOpen ? 'w-72 opacity-100' : 'w-0 opacity-0'
          }`}
        >
          <div className="os-scroll h-full w-72 overflow-y-auto overflow-x-hidden">
            <ScriptWing call={call} user={user} />
          </div>
        </div>

        {/* Center: the fixed footprint. Every view fits inside; nothing
            scrolls, nothing grows. */}
        <div
          className={`relative flex h-64 w-[26rem] max-w-[92vw] flex-col bg-white p-4 shadow-xl transition-all duration-200 dark:!bg-surface-elevated ${
            leftOpen ? 'rounded-l-none' : 'rounded-l-2xl'
          } ${rightOpen ? 'rounded-r-none' : 'rounded-r-2xl'}`}
        >
          {/* Header (persists across every view; the outcome question takes
              it over in place — same height, same slot) */}
          <div className="mb-3 flex items-start justify-between gap-2">
            {viewKey === 'outcome' ? (
              <div className="flex h-9 min-w-0 flex-col justify-center">
                <div className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                  {outcomeStage === 'contact' ? (
                    <span className="flex items-center gap-1.5">
                      <PartyPopper size={15} className="text-emerald-500" />
                      Contact made?
                    </span>
                  ) : (
                    "How'd the call go?"
                  )}
                </div>
                <div className="truncate text-xs text-slate-400">
                  {step.label || step.stepKey} · {lead.leadFirstName}
                </div>
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-2.5">
                {/* The avatar opens BOTH wings at once, and closes them again
                    (Nic 2026-08-14). The two header toggles still work
                    individually; this is the "show me everything" shortcut,
                    since wanting one wing usually means wanting both.
                    Closes only when both are already open, so it completes a
                    half-open state instead of collapsing it. */}
                <button
                  type="button"
                  onClick={() => {
                    const bothOpen = leftOpen && rightOpen;
                    setLeftOpen(!bothOpen);
                    setRightOpen(!bothOpen);
                  }}
                  title={
                    leftOpen && rightOpen
                      ? 'Hide the person and the script'
                      : 'Show the person and the script'
                  }
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                    leftOpen && rightOpen
                      ? 'bg-slate-800 text-white dark:bg-slate-200 dark:!text-slate-900'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15'
                  }`}
                >
                  {initialsOf(lead.leadFirstName, lead.leadLastName)}
                </button>
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                    {lead.leadFirstName} {lead.leadLastName}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                    <span>{statusLabel}</span>
                    {call.callLive ? (
                      <span className="tabular-nums">
                        {formatDuration(elapsedMs)}
                      </span>
                    ) : null}
                    <span>· {step.label || step.stepKey}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="flex shrink-0 items-center gap-1">
              <HeaderToggle
                icon={User}
                open={leftOpen}
                onClick={() => setLeftOpen((v) => !v)}
                title="The person"
              />
              <HeaderToggle
                icon={ScrollText}
                open={rightOpen}
                onClick={() => setRightOpen((v) => !v)}
                title="The script"
              />
              {call.callLive ? (
                <button
                  type="button"
                  onClick={actions.minimize}
                  title="Minimize. The call keeps going"
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  <Minus size={15} />
                </button>
              ) : !call.placed || call.failed ? (
                <button
                  type="button"
                  onClick={actions.closeCenter}
                  title="Close"
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  <X size={15} />
                </button>
              ) : null}
            </div>
          </div>

          {/* Live controls (persist while the call is up) */}
          {call.callLive ? (
            <div className="mb-3 flex items-center gap-2">
              <button
                type="button"
                onClick={actions.toggleMute}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                  muted
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-300'
                }`}
              >
                {muted ? <MicOff size={13} /> : <Mic size={13} />}
                {muted ? 'Muted' : 'Mute'}
              </button>
              <button
                type="button"
                onClick={actions.hangup}
                className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-500"
              >
                <PhoneOff size={13} /> Hang up
              </button>
            </div>
          ) : null}

          {/* The stepped body — one view at a time inside the fixed zone,
              crossfaded on REAL transitions only (key change), never on
              timer ticks. */}
          <div
            key={viewKey}
            className="cc-fade-in flex min-h-0 flex-1 flex-col"
          >
            {viewKey === 'failed' ? (
              <div className="my-auto rounded-xl bg-red-50 px-3.5 py-3 text-sm text-red-600 dark:bg-red-500/10">
                The call could not be placed. Close this and try again.
              </div>
            ) : viewKey === 'precall' ? (
              <div className="my-auto space-y-3 text-center">
                {/* The confirmation (Nic 2026-08-13): before dialing, it is
                    unmistakable that this call rides OUTSIDE the checklist.
                    ONE line — the header already says "Outside the cadence". */}
                {call.outsideCadence ? (
                  <div className="whitespace-nowrap rounded-xl bg-slate-100 px-3.5 py-1.5 text-[11px] font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                    This call won&apos;t count toward any checklist step.
                  </div>
                ) : null}
                <div className="text-2xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatPhone(lead.leadPhone)}
                </div>
                <button
                  type="button"
                  disabled={busy || call.dialing}
                  onClick={actions.placeCall}
                  className="mx-auto flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
                >
                  <Phone size={16} /> Place call
                </button>
                {user?.internalUserPhone ? (
                  <p className="text-[11px] text-slate-400">
                    Calls from your line {formatPhone(user.internalUserPhone)}
                  </p>
                ) : null}
              </div>
            ) : viewKey === 'talking' ? (
              <div className="my-auto space-y-2">
                <MadeContactButton
                  busy={busy}
                  dialing={call.dialing}
                  onMadeContact={actions.madeContact}
                />
                <p className="text-center text-[11px] text-slate-400">
                  Talking to the veteran? Confirm it and the hand-off actions
                  unlock. No answer or voicemail gets recorded after you hang
                  up.
                </p>
              </div>
            ) : viewKey === 'consult' ? (
              <div className="my-auto space-y-3">
                <div className="text-center text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Talking to {call.consult.target.name}. The veteran is on hold.
                </div>
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={actions.completeWarmTransfer}
                    className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    Complete the transfer
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={actions.cancelWarmTransfer}
                    className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-200 dark:!bg-white/10 dark:text-slate-300"
                  >
                    Cancel and return
                  </button>
                </div>
              </div>
            ) : viewKey === 'transfer' ? (
              // A landed transfer records itself (contact made + who it went
              // to) and PARKS the lead in Contact Made — the tile's confirm
              // is what moves them to Lead Handoffs (Nic 2026-08-15). This
              // view is pure confirmation; the manual close only appears if
              // the observe write failed (the retry path).
              <div className="my-auto space-y-3">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 text-sm font-bold text-slate-800 dark:text-slate-100">
                    <Check size={15} className="text-emerald-500" />
                    Call transferred to {call.transferStaged.target.name}
                  </div>
                  <div className="text-xs text-slate-400">
                    {/* The hot seat (Nic 2026-10-23): theirs now, and a miss
                        lands on the specialist's list (RingCentral says). */}
                    {call.declaredOutcome === 'transferred'
                      ? `${(call.transferStaged.target.name || '').split(' ')[0] || 'They'} has them now${
                          call.hotSeatDone?.insteadOf ? ` (was ${call.hotSeatDone.insteadOf.split(' ')[0]}'s)` : ''
                        }. ${call.transferStaged.mode === 'warm' ? 'They were on the line together.' : "If they miss it, it's on their list to call back."}`
                      : 'The call went through, but recording it failed.'}
                  </div>
                </div>
                <div className="flex justify-center">
                  {call.declaredOutcome === 'transferred' ? (
                    <button
                      type="button"
                      onClick={actions.closeCenter}
                      className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-slate-100"
                    >
                      Close
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={actions.confirmTransferOutcome}
                      className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                    >
                      Close it out
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <OutcomeInline
                busy={busy}
                actions={actions}
                stage={outcomeStage}
                setStage={setOutcomeStage}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CallCommandCenter;
